import React, { useCallback, useReducer, useState } from "react"
import { AdFormProgressBanner } from "../../../components/AdForm/Banner/AdFormProgressBanner"
import { AdFormStepContainer } from "../../../components/AdForm/Steps/StepContainer"
import { changeStep } from "../../../components/AdForm/Store/reducers/stepReducer/actions"
import { stepStatus } from "../../../components/AdForm/Store/reducers/stepReducer/initialState"
import {
    formReducer,
    displayReducer,
} from "../../../components/AdForm/Store/reducers/formReducer/actions"
import {
    formValues,
    displayValues,
} from "../../../components/AdForm/Store/reducers/formReducer/initialState"
import { FormValues } from "../../../components/AdForm/Store/reducers/formReducer/types"
import { StepState } from "../../../components/AdForm/Store/reducers/stepReducer/types"
import { submitAdFormRegistration } from "../../api/adFormApi"
import { AdFormOfferInfo } from "../../../components/AdForm/Steps/StepContainer/types"

interface AdFormPageProps {
    offerId: string
    offerOccupationName: string
    offerCategoryName: string
    offerAddress: string
    offerIconUrl: string
    offerShopName: string
    canApply: boolean
    offerType: string
    registerToken?: string
    preUserData?: Partial<FormValues>
}

const AdFormPage = ({
    offerId,
    offerOccupationName,
    offerCategoryName,
    offerAddress,
    offerIconUrl,
    offerShopName,
    canApply,
    offerType,
    registerToken,
    preUserData,
}: AdFormPageProps) => {
    const [isBlocking, setIsBlocking] = useState(false)

    const initialFormState = {
        ...formValues,
        ...(preUserData || {}),
        userResumesAttributes: {
            0: {
                ...formValues.userResumesAttributes[0],
                ...(preUserData?.userResumesAttributes?.[0] || {}),
            },
        },
    } as FormValues

    const [stepState, dispatchStep] = useReducer(changeStep, {
        currentStep: { currentStep: 0 },
        stepStatus: stepStatus,
    })
    const [formState, formDispatch] = useReducer(formReducer, initialFormState)
    const [displayState, displayDispatch] = useReducer(
        displayReducer,
        displayValues
    )

    const setStepStatus = (key: keyof StepState, value: boolean) => {
        dispatchStep({ type: "setStepStatus", key, value })
    }

    const handleNext = () => {
        setIsBlocking(true)
        dispatchStep({ type: "nextStep" })
        setTimeout(() => setIsBlocking(false), 600)
    }

    const handlePrev = () => {
        setIsBlocking(true)
        dispatchStep({ type: "prevStep" })
        setTimeout(() => setIsBlocking(false), 600)
    }

    const setFormValue = (
        formKey: keyof FormValues | ["userResumesAttributes", string],
        inputValue: string | number | number[]
    ) => {
        formDispatch({
            type: "changeFormValue",
            payload: { key: formKey as keyof FormValues, value: inputValue },
        })
    }

    const updateUserResume = (
        index: number,
        formKey: string,
        inputValue: string | number
    ) => {
        formDispatch({
            type: "changeUserResumeValue",
            payload: { index, key: formKey, value: inputValue },
        })
    }

    const setDisplayValue = (
        displayFormKey: string | string[],
        displayFormValue: string | number | (string | number)[]
    ) => {
        displayDispatch({
            type: "displayFormValue",
            payload: {
                displayKey: displayFormKey,
                displayValue: displayFormValue,
            },
        })
    }

    const handleRegistration = useCallback(
        async (data: FormValues) => {
            return submitAdFormRegistration(data, offerId, registerToken)
        },
        [offerId, registerToken]
    )

    const offerInfo: AdFormOfferInfo = {
        occupationName: offerOccupationName,
        categoryName: offerCategoryName,
        address: offerAddress,
        iconUrl: offerIconUrl,
        shopName: offerShopName,
        canApply,
        offerType,
    }

    return (
        <div>
            <AdFormProgressBanner
                currentStep={stepState.currentStep.currentStep}
            />
            <AdFormStepContainer
                stepState={stepState}
                displayState={displayState}
                formState={formState}
                stepBoolean={stepState.stepStatus}
                handleNext={handleNext}
                handlePrev={handlePrev}
                setFormValue={setFormValue}
                updateUserResume={updateUserResume}
                setDisplayValue={setDisplayValue}
                setStepStatus={setStepStatus}
                isBlocking={isBlocking}
                registerToken={registerToken}
                offerId={offerId}
                offerInfo={offerInfo}
                handleRegistration={handleRegistration}
            />
        </div>
    )
}

export default AdFormPage
